import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import T, { Translate } from '../../i18n/i18n';
import { editTableActions, rtcBufferActions, charactersActions } from '../../../redux/actions';
import { updateTable, removeTable, setTableSheet, highlightTable } from '../../../services/tables';
import { templateData } from '../../../services/templates';
import * as Tag from './TableForm.styles';
import Keyboard from '../../../services/keyboardShortcuts';
import TableDataForm from '../../TableDataForm/TableDataForm';
import { ConvertToString } from '../../../services/dates';

export default ({tableId}) => {
    
    const dispatch = useDispatch();
    const history = useHistory();
    const opened = useSelector(state => state.editTableState.opened);
    const loadedData = useSelector(state => state.editTableState.data);
    const callback = useSelector(state => state.editTableState.callback);
    const formElement = useRef(null);

    const [data, setData] = useState({});
    const [isDeleting, setIsDeleting] = useState(false);
    const [isHighlighting, setIsHighlighting] = useState(false);
    const [error, setError] = useState("");
    const [highlightEnddate, setHighlightEnddate] = useState(null);

    const close = () => {
        dispatch(editTableActions.closeWindow());
        setIsDeleting(false);
    }

    const save = async () => {
        if (!data["name"]) {
            return;
        }
        
        if (data["next"] && typeof data["next"] !== 'string') {
            data["next"] = ConvertToString(data["next"]);
        }
        let table = await updateTable(tableId, data);

        if(table.status === 'error') {
            setError(table.payload.error ? table.payload.error.message : table.payload);
        } else {
            if (data && data.charsheetTemplate) {
                const selectedTemplate = await templateData(data.charsheetTemplate.id);
                await setTableSheet(tableId, data.charsheetTemplate.id);

                dispatch(rtcBufferActions.pushOutputBuffer({
                    type: 'templateSync',
                    data: selectedTemplate
                }));
                dispatch(charactersActions.setSheetTemplate(selectedTemplate));
            }
            callback(data);
            close();
        }
    }

    const performDelete = async (e) => {
        if (!isDeleting) {
            return false;
        }

        let resp = await removeTable(tableId);
        if (resp.status === 'error') {
            setError(resp.payload.error ? resp.payload.error.message : resp.payload);
        } else {
            history.goBack();    
        }
    }

    const performHighlight = async (e) => {
        if (!isHighlighting) {
            return false;
        }

        let resp = await highlightTable(tableId);
        if (resp.status === 'error') {
            setError(resp.payload.message && resp.payload.message.error ? resp.payload.message.error.message : "");
        } else {
            setHighlightEnddate(resp.endDate);
            setData(d => ({...d, highlight: {endDate: resp.endDate}}));
        }

        setIsHighlighting(false);
        formElement.current.scrollTo(0,0);
    }

    const keyCancel = (e) => {
        e.stopPropagation();
    }

    useEffect(() => {
        if (!opened) {
            return;
        }

        let escapeKey = () => {
            dispatch(editTableActions.closeWindow());
            return false;
        };

        Keyboard.register({key:'ESCAPE'}, escapeKey);

        return () => {
            Keyboard.unregister({key:'ESCAPE'}, escapeKey);
        }
    }, [opened, dispatch]);

    useEffect(() => {
        setData(loadedData);
        if (loadedData) {
            setHighlightEnddate(loadedData.highlight ? loadedData.highlight.endDate : null);
        }
    }, [loadedData]);

    return <>
        <Tag.Background onKeyDown={keyCancel} className={opened ? 'opened' : ''} onClick={() => {close()}} />
        <Tag.Window onKeyDown={keyCancel} className={opened ? 'opened' : ''}>
            <Tag.Header>
                <Tag.HeaderTitle><T value="tableform.title-edit"/></Tag.HeaderTitle>
            </Tag.Header>

            <Tag.Content className='scrollbar' ref={formElement}>
                <TableDataForm 
                    padding={'10px 20px 20px'}
                    onChange={setData}
                    value={data}
                    error={error}
                />
            </Tag.Content>
            
            <Tag.ButtonBar>
                <Tag.ButtonRow justify="flex-start">
                    {isDeleting ? <>
                        <Tag.Alert><T value="tables.confirmremove" /></Tag.Alert>
                        <Tag.Button color="#f33" onClick={()=>performDelete()}><T value="yes" /></Tag.Button>
                        <Tag.Button color="#0c0" onClick={()=>setIsDeleting(false)}><T value="no" /></Tag.Button>
                    </> : null}

                    {isHighlighting ? <>
                        <Tag.Alert style={{maxWidth: "450px"}}><T value="tables.confirmhighlight" /></Tag.Alert>
                        <Tag.Button color="#0c0" onClick={()=>performHighlight()}><T value="yes" /></Tag.Button>
                        <Tag.Button color="#f33" onClick={()=>setIsHighlighting(false)}><T value="no" /></Tag.Button>
                    </> : null}

                    {!isDeleting && !isHighlighting ? <Tag.ButtonRow>
                        <Tag.Button color="#f33" onClick={()=>setIsDeleting(true)}><T value="delete" /></Tag.Button>
                        {data && data.highlight === null ? 
                            <Tag.Button color="#29f" onClick={()=>setIsHighlighting(true)}><T value="tables.highlight" /></Tag.Button>
                        :
                            <Tag.Alert>{ Translate("tables.highlightUntil")}<br />{ highlightEnddate }</Tag.Alert>
                        }
                            
                    </Tag.ButtonRow> : null}
                </Tag.ButtonRow>
                {isDeleting || isHighlighting ? null : <Tag.ButtonRow justify="flex-end">
                    <Tag.Button color="#999" onClick={() => {close()}} >< T value="cancel" /></Tag.Button>
                    <Tag.Button color="#26f" onClick={() => {save()}} >< T value="save" /></Tag.Button>
                </Tag.ButtonRow>}
            </Tag.ButtonBar>
        </Tag.Window>
    </>;

};